import classNames from 'classnames';
import { CarouselController } from '../../../components/Carousel';
import ImageGallery from '../../../components/ImageGallery/ImageGallery';
import React from 'react';
import styles from '../../../views/ProductDetails/Series3CollarDetails/components/ImageGallery/ImageGallery.module.scss';
import { Species } from '../../../types/gql-op-types';
import { IImage, IVideo } from '../../../types';
import { showRescueGwaVariant, showRescuePdpRedesign } from '../../../views/RescueLanding/RescueBanner';
import { useLocation } from 'react-router-dom';
import AppPaths from '../../../AppPaths';
import { useIsGift } from '../../../lib/util/isGift';

// composite hero from the 2026-07-28 rescue pdp prototype: the mini held in hand over the app
// showing location and activity. adopters have never seen the app, so it leads their gallery.
// the asset itself was carried over from the prototype and needs design sign-off before this
// variant takes real traffic.
const RESCUE_HERO_IMAGE: IImage = {
  type: 'image',
  url: '/product_images/mini/rescue-hero.jpg',
  retinaWidth: 1500,
  retinaHeight: 1500,
};

// the five silicone covers the Mini ships with. GwP (/miniredeem) and GwA (adoption) shoppers
// arrive from a partner or shelter link rather than from the shop, so the gallery is the only place
// they are told the collar comes in colors - it sits third in both of those galleries, close enough
// to the front that a shopper meets it without paging to the back of the carousel.
const COLOR_PACK_IMAGE: IImage = {
  type: 'image',
  url: '/product_images/mini/mini-color-pack.jpg',
  descriptors: ['1500w', '3000w', '4000w'],
};

/** Url of the color pack slide, for callers that need to find it by index. See `MiniPDP`. */
export const COLOR_PACK_IMAGE_URL = COLOR_PACK_IMAGE.url;

interface DesktopImageGalleryProps {
  carouselControllerRef?: React.MutableRefObject<CarouselController | undefined>;
  onChange?: (index: number) => void;
  children?: React.ReactNode;
  species: Species;
  /** Dynamic images from API - if provided, overrides hardcoded images */
  dynamicImages?: (IImage | IVideo)[] | null;
  /** Replaces the product photo the gallery opens with. */
  heroImage?: IImage;
  prevNextButtons?: boolean;
  /** Glide to the tapped thumbnail instead of cutting to it. */
  animateThumbnailSelect?: boolean;
}

function getMedia(species: Species, updatedImages = false, isGift: boolean): IImage[] {
  if (isGift) {
    return [
      {
        type: 'image',
        url: `/product_images/mini/gift/mini-01.jpg`,
        descriptors: ['1500w', '3000w', '4000w'],
      },
    ];
  }

  if (updatedImages) {
    const limit = species === Species.CAT ? 5 : 7;

    return Array.from({ length: limit }, (_, i) => ({
      type: 'image',
      url: `/product_images/mini/experiment/mini-0${i + 1}-${species.toLowerCase()}.jpg`,
      descriptors: ['1500w', '3000w', '4000w'],
    }));
  }

  // Mini relaunch photography (2026-08). The dog set carries one more frame than
  // the cat set, so the count is species-dependent — same shape as the experiment
  // branch above.
  const limit = species === Species.CAT ? 5 : 6;

  return Array.from({ length: limit }, (_, i) => ({
    type: 'image' as const,
    url: `/product_images/mini/mini-0${i + 1}-${species.toLowerCase()}.jpg`,
    descriptors: ['1500w', '3000w', '4000w'],
  }));
}

/**
 * The Mini gallery's media list, in carousel order.
 *
 * Exported as a hook rather than computed inline because `MiniPDP` needs to find a slide by index:
 * the light-mode nav treatment is keyed on the selected index, and the position of the color pack
 * slide moves with species, path and whether the rescue hero is prepended.
 */
export function useMiniGalleryMedia(
  species: Species,
  dynamicImages?: (IImage | IVideo)[] | null,
  heroImage?: IImage,
): (IImage | IVideo)[] {
  /*
   * While we're still running the experiment for the updated Mini images on the general PDP,
   * we definitely want to use them on the Gift With Purhcase page.
   */
  const { pathname } = useLocation();
  const updatedMiniImages = pathname.startsWith(AppPaths.MiniRedeem);
  const isGift = useIsGift();

  // Use dynamic images if provided, otherwise fall back to hardcoded
  const galleryMedia = dynamicImages ?? getMedia(species, updatedMiniImages, isGift);
  // swapped before the rescue hero is prepended, so an override always lands on the product photo
  const baseMedia = heroImage ? [heroImage, ...galleryMedia.slice(1)] : galleryMedia;
  const media = showRescuePdpRedesign() && !isGift ? [RESCUE_HERO_IMAGE, ...baseMedia] : baseMedia;

  // GwP and GwA only. `showRescueGwaVariant()` rather than `showRescuePdpRedesign()` deliberately:
  // the redesign flag carries a 50/50 experiment, and hanging an extra slide off one arm of it
  // would put a gallery change inside a layout test.
  const showColorPack = !isGift && (updatedMiniImages || showRescueGwaVariant());

  // third, not first: the two opening slides still have to be the collar on a dog.
  const COLOR_PACK_POSITION = 2;

  return showColorPack
    ? [...media.slice(0, COLOR_PACK_POSITION), COLOR_PACK_IMAGE, ...media.slice(COLOR_PACK_POSITION)]
    : media;
}

export function DesktopImageGallery({
  carouselControllerRef,
  onChange,
  children,
  species,
  dynamicImages,
  heroImage,
  prevNextButtons,
  animateThumbnailSelect,
}: DesktopImageGalleryProps) {
  const isGift = useIsGift();
  const media = useMiniGalleryMedia(species, dynamicImages, heroImage);

  return (
    <div className={classNames(styles.imageGalleryContainer, styles.desktopImageGallery)} style={{ borderRadius: 0 }}>
      {children}
      <ImageGallery
        parentCarouselControllerRef={carouselControllerRef}
        media={media}
        pageDots={true}
        disableAnimation={!animateThumbnailSelect}
        friction={0.4}
        onChange={onChange}
        noBorderRadius={true}
        // isGift uses FiImage, which requires square aspect ratio. All other Mini images don't need this.
        squareAspectRatio={isGift}
        prevNextButtons={prevNextButtons}
      />
    </div>
  );
}
